import { CheckCircle2, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { TagInput } from '@nucleus-commerce/admin-kit/forms';
import type { BulkOrderAction, BulkOrdersResult } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useBulkOrders } from '../hooks/use-order-mutations';
import {
  bulkFailures,
  bulkSchema,
  emptyBulkForm,
  toBulkBody,
  type BulkForm,
} from '../schemas/bulk-schema';

export interface BulkOrdersDialogProps {
  storeId: string;
  action: BulkOrderAction;
  /** Ids with the order name each was shown as, for the result list. */
  orders: ReadonlyArray<{ id: string; name: string }>;
  canRefund: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Asks what the chosen bulk action needs, runs it on the selected orders and shows what happened
 * to each: the backend runs every order on its own, so some can fail while the rest go through.
 */
export function BulkOrdersDialog({
  storeId,
  action,
  orders,
  canRefund,
  onOpenChange,
}: BulkOrdersDialogProps) {
  const { t } = useTranslation(['orders', 'errors', 'common']);
  const message = useErrorMessage();
  const bulk = useBulkOrders(storeId);
  const [result, setResult] = useState<BulkOrdersResult | null>(null);
  const schema = useMemo(() => bulkSchema(action), [action]);
  const form = useForm<BulkForm>({
    resolver: useI18nResolver(schema),
    defaultValues: emptyBulkForm(),
  });
  const names = new Map(orders.map((order) => [order.id, order.name]));

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      setResult(
        await bulk.mutateAsync(
          toBulkBody(
            action,
            orders.map((o) => o.id),
            schema.parse(values),
          ),
        ),
      );
    } catch (error) {
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  const failures = result ? bulkFailures(result) : [];

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(`orders:bulk.${action}.title`, { count: orders.length })}</DialogTitle>
          <DialogDescription>
            {result
              ? t('orders:bulk.result.summary', {
                  succeeded: result.succeeded,
                  failed: result.failed,
                })
              : t(`orders:bulk.${action}.description`)}
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="flex flex-col gap-3">
            <p className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="text-success size-4" aria-hidden />
              {t('orders:bulk.result.succeeded', { count: result.succeeded })}
            </p>
            {failures.length > 0 ? (
              <ul
                className="flex flex-col gap-2 text-sm"
                aria-label={t('orders:bulk.result.failedList')}
              >
                {failures.map((failure) => (
                  <li key={failure.id} className="flex items-start gap-2">
                    <XCircle className="text-destructive mt-0.5 size-4 shrink-0" aria-hidden />
                    <span>
                      <span className="font-mono font-medium">
                        {names.get(failure.id) ?? failure.id}
                      </span>
                      {': '}
                      {failure.code
                        ? t(`errors:code.${failure.code}`, {
                            defaultValue: failure.message ?? failure.code,
                          })
                        : failure.message}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                {t('common:actions.close')}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={submit} noValidate className="flex flex-col gap-4">
              {form.formState.errors.root?.server?.message ? (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
                </Alert>
              ) : null}
              {action === 'add_tags' || action === 'remove_tags' ? (
                <FormField
                  control={form.control}
                  name="tags"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('orders:notes.tags')}</FormLabel>
                      <TagInput
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={t('orders:notes.tagsPlaceholder')}
                        removeLabel={(tag) => t('orders:notes.removeTag', { tag })}
                        max={50}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
              {action === 'mark_paid' ? (
                <>
                  <FormField
                    control={form.control}
                    name="reference"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('orders:markPaid.reference')}</FormLabel>
                        <FormControl>
                          <Input autoComplete="off" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="note"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('orders:markPaid.note')}</FormLabel>
                        <FormControl>
                          <Textarea rows={2} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              ) : null}
              {action === 'fulfil' ? (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="carrier"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('orders:fulfil.carrier')}</FormLabel>
                          <FormControl>
                            <Input autoComplete="off" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="trackingNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('orders:fulfil.trackingNumber')}</FormLabel>
                          <FormControl>
                            <Input autoComplete="off" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="trackingUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('orders:fulfil.trackingUrl')}</FormLabel>
                        <FormControl>
                          <Input type="url" autoComplete="off" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="notifyCustomer"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border p-3">
                        <FormLabel>{t('orders:fulfil.notify')}</FormLabel>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </>
              ) : null}
              {action === 'cancel' ? (
                <>
                  <FormField
                    control={form.control}
                    name="reason"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('orders:cancel.reason')}</FormLabel>
                        <FormControl>
                          <Textarea rows={2} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="restock"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(checked) => field.onChange(checked === true)}
                          />
                        </FormControl>
                        <FormLabel className="font-normal">{t('orders:cancel.restock')}</FormLabel>
                      </FormItem>
                    )}
                  />
                  {canRefund ? (
                    <FormField
                      control={form.control}
                      name="refund"
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-1">
                          <div className="flex flex-row items-center gap-2">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={(checked) => field.onChange(checked === true)}
                              />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {t('orders:cancel.refund')}
                            </FormLabel>
                          </div>
                          <FormDescription>{t('orders:cancel.refundHint')}</FormDescription>
                        </FormItem>
                      )}
                    />
                  ) : null}
                </>
              ) : null}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={bulk.isPending}
                  variant={action === 'cancel' ? 'destructive' : 'default'}
                >
                  {bulk.isPending
                    ? t('orders:bulk.submitting')
                    : t(`orders:bulk.${action}.submit`, { count: orders.length })}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
